import { useState, useEffect } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { CustomScrollbarWrapper } from 'cdk-radial';

interface Props {
  text: string;
  onClose: () => void;
  title?: string;
  disclaimer?: string;
  loadingPrefix?: string;
  showDisclaimer?: boolean;
  onNotificationClick?: (id: string) => void;
}

const NOTIFICATION_LINK_PREFIX = 'notification:';

function SummaryStarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ marginLeft: '4px' }}>
      <path
        d="M12 2L14.2 9.8L22 12L14.2 14.2L12 22L9.8 14.2L2 12L9.8 9.8L12 2Z"
        fill="#196FE1"
      />
      <path
        d="M18 4L18.8 6.8L21.5 7.5L18.8 8.2L18 11L17.2 8.2L14.5 7.5L17.2 6.8L18 4Z"
        fill="#4DA3FF"
      />
    </svg>
  );
}

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) =>
    theme.darkMode ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.2)'};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: clamp(24px, calc(100vw - 744px), 444px);
  z-index: 10999;
`;

export const Popup = styled.div`
  background-color: ${({ theme }) => (theme.darkMode ? '#1A1A1A' : '#fff')};
  padding: 20px;
  width: 720px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  color: ${({ theme }) => (theme.darkMode ? '#E0E0E0' : '#333')};
`;

export const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 20px;
  line-height: 1;
  color: ${({ theme }) => (theme.darkMode ? '#E0E0E0' : '#333')};
`;

// Uses cdk-radial's CustomScrollbarWrapper so the scroll bar matches the
// notification sidesheet's styled scrollbar.
export const PopupContent = styled(CustomScrollbarWrapper)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${({ theme }) => (theme.darkMode ? '#1A1A1A' : '#fff')};
  color: ${({ theme }) => (theme.darkMode ? '#E0E0E0' : '#333')};
`;

export const NotificationSummaryPopup = ({
  text,
  onClose,
  title,
  disclaimer = '*AI-generated summary based on your notifications. AI can make mistakes.',
  loadingPrefix = 'Thinking',
  showDisclaimer: showDisclaimerProp = true,
  onNotificationClick
}: Props) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const loadingText = 'Loading';

    if (text !== loadingText) {
      setDisplayedText(text);
      setShowDisclaimer(true);
      return;
    }

    let dotCount = 0;
    const maxDots = 15;

    const interval = setInterval(() => {
      dotCount = (dotCount + 1) % (maxDots + 1);
      setDisplayedText(loadingPrefix + '.'.repeat(dotCount));
    }, 200);

    return () => clearInterval(interval);
  }, [text, loadingPrefix]);

  return (
    <Overlay
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Popup>
        <PopupHeader>
          {title && (
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
              {title}
              <SummaryStarIcon />
            </h3>
          )}
          <CloseButton
            type="button"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </CloseButton>
        </PopupHeader>
        <PopupContent>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          urlTransform={(url) =>
            url.startsWith(NOTIFICATION_LINK_PREFIX)
              ? url
              : defaultUrlTransform(url)
          }
          components={{
            h3: ({ children }) => (
              <h3 style={{ marginTop: 0, marginBottom: '12px' }}>{children}</h3>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: '6px' }}>{children}</li>
            ),
            p: ({ children }) => (
              <p style={{ marginBottom: '10px' }}>{children}</p>
            ),
            strong: ({ children }) => (
              <strong style={{ fontWeight: 600 }}>{children}</strong>
            ),
            a: ({ href, children }) => {
              if (href && href.startsWith(NOTIFICATION_LINK_PREFIX)) {
                const id = href.slice(NOTIFICATION_LINK_PREFIX.length);
                return (
                  <button
                    type="button"
                    onClick={() => onNotificationClick?.(id)}
                    style={{
                      color: '#1E6BFF',
                      cursor: 'pointer',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      textDecoration: 'underline'
                    }}
                  >
                    {children}
                  </button>
                );
              }
              return <>{children}</>;
            }
          }}
        >
          {displayedText}
        </ReactMarkdown>
        </PopupContent>
        {showDisclaimer && showDisclaimerProp && (
          <p style={{ opacity: 0.6, fontSize: '10px', marginTop: '20px' }}>
            {disclaimer}
          </p>
        )}
      </Popup>
    </Overlay>
  );
};
